import React, { useState, useEffect } from "react";
import {
  useNavigate,
  useSearchParams,
  Link as RouterLink,
} from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Link,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { AuthService } from "../../services/auth/auth.service";
import { handleApiError } from "../../utils/error-handler";
import {
  PASSWORD_MAX_LENGTH,
  PASSWORD_MAX_LENGTH_MESSAGE_KEY,
  PASSWORD_MIN_LENGTH,
  PASSWORD_MIN_LENGTH_MESSAGE_KEY,
  PASSWORD_PATTERN,
  PASSWORD_PATTERN_MESSAGE_KEY,
} from "../../utils/password-policy";

/**
 * Схема сброса пароля — ФАБРИКА, а не константа модуля (AD-44).
 *
 * Правила зеркалят единую BE-политику (ADR-AUTH-unified-password-policy) — см.
 * password-policy.ts; раньше FE проверял min(6) без сложности, и BE отвечал
 * «Validation failed» после сабмита. Верхняя граница добавлена здесь же: она
 * была объявлена политикой и проверялась при регистрации, а на сбросе — нет,
 * то есть длинный пароль снова упирался в молчаливый отказ BE.
 *
 * Сообщения берутся у `t` в момент сборки схемы. Константа уровня модуля
 * зафиксировала бы язык первого импорта: до правки здесь стояли русские
 * литералы, и немецкий экран сброса печатал «Новый пароль обязателен».
 */
const createResetPasswordSchema = (t: TFunction<"common">) =>
  yup.object({
    password: yup
      .string()
      .required(t("auth.validation.newPasswordRequired"))
      .min(
        PASSWORD_MIN_LENGTH,
        t(PASSWORD_MIN_LENGTH_MESSAGE_KEY, { count: PASSWORD_MIN_LENGTH }),
      )
      .max(
        PASSWORD_MAX_LENGTH,
        t(PASSWORD_MAX_LENGTH_MESSAGE_KEY, { count: PASSWORD_MAX_LENGTH }),
      )
      .matches(PASSWORD_PATTERN, t(PASSWORD_PATTERN_MESSAGE_KEY)),
    confirmPassword: yup
      .string()
      .required(t("auth.validation.confirmPasswordRequired"))
      .oneOf([yup.ref("password")], t("auth.validation.passwordsMismatch")),
  });

type ResetPasswordFormData = yup.InferType<
  ReturnType<typeof createResetPasswordSchema>
>;

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  // Токен приходит query-параметром: письмо ведёт на
  // `${FRONTEND_URL}/auth/reset-password?token=…` (email.service.sendPasswordResetEmail).
  // Раньше читался через useParams (path) → token всегда undefined → «Отсутствует токен». TD-21.
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);

  useEffect(() => {
    // Здесь можно добавить проверку валидности токена при необходимости
    if (!token) {
      setTokenValid(false);
      setError(t("auth.resetPasswordPage.missingToken"));
    }
  }, [token, t]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: yupResolver(createResetPasswordSchema(t)),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) return;

    try {
      setLoading(true);
      setError(null);
      await AuthService.resetPassword(token, data.password);
      setSuccess(true);
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err: unknown) {
      setError(handleApiError(err, t("auth.resetPasswordPage.resetError")));
    } finally {
      setLoading(false);
    }
  };

  if (!tokenValid) {
    return (
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "calc(100vh - 64px)",
        }}
      >
        <Paper
          sx={{
            maxWidth: 480,
            width: "100%",
            p: 4,
            boxShadow: (theme) => theme.shadows[3],
          }}
        >
          <Typography variant="h4" align="center" gutterBottom>
            {t("auth.resetPasswordPage.errorTitle")}
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || t("auth.resetPasswordPage.invalidLink")}
          </Alert>
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Link component={RouterLink} to="/forgot-password" variant="body2">
              {t("auth.resetPasswordPage.requestNewLink")}
            </Link>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "calc(100vh - 64px)",
      }}
    >
      <Paper
        sx={{
          maxWidth: 480,
          width: "100%",
          p: 4,
          boxShadow: (theme) => theme.shadows[3],
        }}
      >
        <Typography variant="h4" align="center" gutterBottom>
          {t("auth.resetPasswordPage.title")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              {t("auth.resetPasswordPage.success")}
            </Alert>
            <CircularProgress
              size={24}
              sx={{ display: "block", margin: "0 auto" }}
            />
          </>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Typography variant="body2" sx={{ mb: 3 }}>
              {t("auth.resetPasswordPage.intro")}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("auth.resetPasswordPage.newPassword")}
                    type="password"
                    fullWidth
                    variant="outlined"
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    disabled={loading}
                  />
                )}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("auth.resetPasswordPage.confirmPassword")}
                    type="password"
                    fullWidth
                    variant="outlined"
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                    disabled={loading}
                  />
                )}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("auth.resetPasswordPage.submit")
                )}
              </Button>
            </Box>

            <Box sx={{ textAlign: "center" }}>
              <Link component={RouterLink} to="/login" variant="body2">
                {t("auth.resetPasswordPage.backToLogin")}
              </Link>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default ResetPasswordPage;
